/**
 * Progression de lecture (C11 SP3) — état Redux alimenté par le service
 * `readingProgressAPI`. Mise à jour locale optimiste dès l'envoi (§ 4.2) :
 * la carte « Reprendre » et la pastille de progression n'attendent pas le serveur.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import { listReadingProgress, saveReadingProgress, type ReadingProgressDto, type ReadingProgressInput, type ReadingFileType } from '../../services/readingProgressAPI';

export type { ReadingProgressDto, ReadingProgressInput, ReadingFileType };

/** Formats que le lecteur mobile sait ouvrir (spec § 2.3 ; PDF depuis SP3 bis). */
export const MOBILE_READABLE_TYPES: ReadonlySet<ReadingFileType> = new Set<ReadingFileType>(['epub', 'html', 'docx', 'pdf']);

interface ReadingProgressState { data: ReadingProgressDto[]; status: string }
const initialState: ReadingProgressState = { data: [], status: LOADING_STATES.IDLE };

export const fetchReadingProgressAsync = createAsyncThunk<ReadingProgressDto[], any>('readingProgress/fetch', async (instance) => {
  const token = await getTokenRedirect(tokenRequest, instance);
  if (!token) return [];
  return listReadingProgress(token);
});

export interface SaveReadingProgressArgs { fileId: string; fileType: ReadingFileType; displayName: string; input: ReadingProgressInput; instance: any }

export const saveReadingProgressAsync = createAsyncThunk<ReadingProgressDto, SaveReadingProgressArgs>('readingProgress/save', async ({ fileId, input, instance }) => {
  const token = await getTokenRedirect(tokenRequest, instance);
  if (!token) throw new Error('Impossible d\'obtenir le jeton d\'accès');
  return saveReadingProgress(token, fileId, input);
});

// Remplace en place si le fichier est déjà connu, sinon ajoute en fin de liste.
// L'ordre de la liste reflète celui du serveur (fetch) ; le « plus récent » se
// calcule à part (`selectLatestReadingProgress`), il ne dépend pas de cet ordre.
function upsert(data: ReadingProgressDto[], next: ReadingProgressDto): ReadingProgressDto[] {
  const index = data.findIndex(p => p.fileId === next.fileId);
  if (index === -1) return [...data, next];
  const copy = [...data];
  copy[index] = next;
  return copy;
}

const readingProgressSlice = createSlice({
  name: 'readingProgress',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReadingProgressAsync.pending, (state) => { state.status = LOADING_STATES.LOADING; })
      .addCase(fetchReadingProgressAsync.fulfilled, (state, action) => { state.data = action.payload; state.status = LOADING_STATES.SUCCEEDED; })
      .addCase(fetchReadingProgressAsync.rejected, (state) => { state.status = LOADING_STATES.FAILED; })
      // Mise à jour locale immédiate : la pastille et la carte « Reprendre » n'attendent pas le serveur.
      .addCase(saveReadingProgressAsync.pending, (state, action) => {
        const { fileId, fileType, displayName, input } = action.meta.arg;
        const previous = state.data.find(p => p.fileId === fileId);
        state.data = upsert(state.data, {
          fileId, fileType, displayName: previous?.displayName ?? displayName,
          cfi: input.cfi ?? previous?.cfi, chapterLabel: input.chapterLabel ?? previous?.chapterLabel,
          page: input.page ?? previous?.page,
          percent: input.percent, updatedDate: new Date().toISOString(),
        });
      })
      .addCase(saveReadingProgressAsync.fulfilled, (state, action) => { state.data = upsert(state.data, action.payload); })
      // Échec silencieux côté UI (spec § 6) : ni interruption ni toast, mais une
      // trace en console pour ne pas perdre l'information en diagnostic.
      .addCase(saveReadingProgressAsync.rejected, (_state, action) => {
        console.warn('[readingProgress] écriture impossible :', action.error?.message ?? action.error);
      });
  },
});

const NO_PROGRESS: ReadingProgressDto[] = [];
export const selectReadingProgressList = (state: any): ReadingProgressDto[] => state.readingProgress?.data ?? NO_PROGRESS;
export const selectReadingProgressByFile = (fileId: string | undefined) => (state: any): ReadingProgressDto | undefined =>
  fileId ? selectReadingProgressList(state).find(p => p.fileId === fileId) : undefined;
/**
 * Statut du chargement global de la progression (spec C11 SP3) : le lecteur
 * (`ReaderScreen`) attend qu'il quitte `LOADING` avant de figer sa cible
 * initiale — sur un démarrage à froid direct sur `/lire/:fileId`, la
 * progression est encore en vol (chargée par `MobileLoader`) au premier rendu.
 */
export const selectReadingProgressStatus = (state: any): string => state.readingProgress?.status ?? LOADING_STATES.IDLE;
export const selectLatestReadingProgress = (state: any): ReadingProgressDto | undefined => {
  const list = selectReadingProgressList(state);
  return list.length === 0 ? undefined : list.reduce((latest, item) => (item.updatedDate > latest.updatedDate ? item : latest));
};

export default readingProgressSlice.reducer;
